import { ReactElement } from 'react';
import { Field, Image, ImageField } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { useRouter } from 'next/router';

import { mapSharedPropertiesToFields } from '../../getters';
import { ComponentProps } from '../../lib/component-props';
import { GNElliotWeb } from '../../public';
import { useBreakpoint } from '../../utils';
import { withTheme } from '../../utils/contexts/jabraTheme/withTheme';
import { useDynamicSvgIcon } from '../../utils/hooks/useDynamicSvgIcon';
import { BtnProps, Button } from '../Button/Button';

export type TopBannerSize = 'extra-small' | 'small' | 'medium' | 'large';

export type TopBannerFields = {
  title: Field<string>;
  titleSecondary: Field<string>;
  details: Field<string>;
  size: Field<TopBannerSize>;
  backgroundColor: Field<string>;
  backgroundImage: ImageField;
  primaryButtonText: Field<string>;
  primaryButtonLink: Field<string>;
  primaryButtonIcon: Field<string>;
  secondaryButtonText: Field<string>;
  secondaryButtonLink: Field<string>;
  secondaryButtonIcon: Field<string>;
};
export type TopBannerProps = ComponentProps & {
  fields: TopBannerFields;
  customComponent?: ReactElement;
};

const imageSizes: { [key in TopBannerSize]: { h: number; w: number } } = {
  ['extra-small']: {
    h: 350,
    w: 708,
  },
  small: {
    h: 420,
    w: 708,
  },
  medium: {
    h: 640,
    w: 708,
  },
  large: {
    h: 900,
    w: 920,
  },
};

const buttonSizes: { [key: string]: BtnProps['size'] } = {
  ['extra-small']: 's',
  small: 'm',
  medium: 'l',
  large: 'l',
};

type classNamesBySize = Partial<{ [key in TopBannerSize]: string }>;

const secondaryTitleClassNames: classNamesBySize = {
  ['extra-small']: 'leading-5 text-xl block',
  small: 'text-base',
  medium: 'text-base',
  large: 'text-xl',
};

const titleClassNames: classNamesBySize = {
  ['extra-small']: 'text-4xl pt-3',
  small: 'text-5xl py-5',
  medium: 'text-5xl py-5',
  large: 'md:text-[5rem] text-5xl py-5',
};

const detailsClassNames: classNamesBySize = {
  small: 'md:text-xl',
  medium: 'md:text-xl',
};

const actionContainerClassNames: classNamesBySize = {
  ['extra-small']: 'pt-6',
  small: 'pt-8',
  medium: 'pt-8',
  large: 'pt-10',
};

const Banner = ({ fields, customComponent }: TopBannerProps) => {
  const { push } = useRouter();
  const { isBelowMd } = useBreakpoint('md', true);
  const size = fields.size.value;
  const buttonSize = buttonSizes[size] || (isBelowMd ? 'l' : 'm');

  const mappedFields = mapSharedPropertiesToFields<TopBannerFields>(fields);

  const { svgElement: primaryRightIcon } = useDynamicSvgIcon(
    fields.primaryButtonIcon?.value,
    'none'
  );
  const { svgElement: secondaryRightIcon } = useDynamicSvgIcon(
    fields.secondaryButtonIcon?.value,
    'none'
  );

  return (
    <div
      className={classNames('bg-theme relative flex justify-end', GNElliotWeb.className)}
      role="banner"
      aria-label={mappedFields.title?.value}
    >
      <div className="left-0 right-0 z-20 m-auto flex h-full w-full xl:container max-xl:flex-col xl:absolute xl:items-center">
        <div className="container-left flex-shrink-0 xl:hidden">
          <Image
            field={{
              src: mappedFields.backgroundImage.value?.src,
              alt: mappedFields.backgroundImage.value?.alt,
            }}
            className="h-[unset] max-h-[550px] w-full rounded-l-full object-cover max-sm:max-h-[400px]"
            height={imageSizes[size].h}
            width={imageSizes[size].w}
          />
        </div>
        <div
          className={classNames('basis-6/12 pb-16 pt-8 max-xl:container', {
            ['max-md:pb-10']: size === 'extra-small',
          })}
        >
          <span
            className={classNames(
              secondaryTitleClassNames[size],
              'pb-3 text-[1.125rem] font-bold uppercase'
            )}
          >
            {mappedFields.titleSecondary.value}
          </span>
          <div className="h-0.5 w-24 bg-yellow" />
          <h3 className={classNames(titleClassNames[size], 'font-bold')}>
            {mappedFields.title?.value}
          </h3>
          <p className={classNames(detailsClassNames[size], 'text-2xl')}>
            {mappedFields.details.value}
          </p>
          <div className={classNames(actionContainerClassNames[size])}>
            {!customComponent && (
              <div className="flex flex-wrap gap-6">
                {mappedFields.primaryButtonText.value && mappedFields.primaryButtonLink.value && (
                  <Button
                    variant="primary"
                    size={buttonSize}
                    onClick={() => push(mappedFields.primaryButtonLink.value)}
                    {...(primaryRightIcon && {
                      icon: primaryRightIcon,
                      iconPlacement: 'right',
                    })}
                  >
                    {mappedFields.primaryButtonText.value}
                  </Button>
                )}
                {mappedFields.secondaryButtonText.value &&
                  mappedFields.secondaryButtonLink.value && (
                    <Button
                      variant="secondary"
                      size={buttonSize}
                      onClick={() => push(mappedFields.secondaryButtonLink.value)}
                      {...(secondaryRightIcon && {
                        icon: secondaryRightIcon,
                        iconPlacement: 'right',
                      })}
                    >
                      {mappedFields.secondaryButtonText.value}
                    </Button>
                  )}
              </div>
            )}
            {customComponent}
          </div>
        </div>
      </div>
      <div style={{ height: `${imageSizes[size].h}px` }} className="basis-7/12 max-xl:hidden">
        <Image
          field={{
            src: mappedFields.backgroundImage.value?.src,
            alt: mappedFields.backgroundImage.value?.alt,
          }}
          className="h-full rounded-l-full object-cover"
          height={imageSizes[size].h}
          width="100%"
        />
      </div>
    </div>
  );
};

export const TopBanner = withTheme<TopBannerProps>({
  legacyBackgroundFieldName: 'backgroundColor',
})(Banner);
